import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, ListItem, ListItemButton, ListItemText, MenuItem, TextField, Typography, } from "@mui/material";
import { queryClient } from "@ssf/components";
import { SpecialType } from "@ssf/memdb-api";
import { useSnackbar } from "notistack";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router";
import { z } from "zod";
import { getIssues, participantsFromTournamentsQuery, useCreateContender, useSchoolName, } from "../../lib/index.js";
import { IssuesIcon } from "../index.js";
const stateSchema = z.object({
    clubId: z.coerce.number().optional(),
});
export const RegTeamItem = ({ club, contenders, groups, isRegable, teamLink, isClassTournament, tournamentIds, }) => {
    const { t } = useTranslation("RegTeamItem");
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { data: school } = useSchoolName(club.schoolid, {
        enabled: Boolean(club.schoolid),
    });
    const { state } = useLocation();
    const stateClubId = useMemo(() => {
        const parsed = stateSchema.safeParse(state);
        if (parsed.success) {
            return parsed.data.clubId;
        }
    }, [state]);
    const contender = contenders[club.id];
    const teamId = contender?.team.id.toString();
    const tournamentId = contender?.tournamentId.toString();
    const [isOpen, setOpen] = useState(!contender && stateClubId === club.id);
    const { mutate: createContender, isPending } = useCreateContender({
        onSuccess({ result }, { tournamentid }) {
            queryClient.invalidateQueries(participantsFromTournamentsQuery(tournamentIds));
            enqueueSnackbar(t("success", { name: club.name }).toString(), {
                variant: "info",
            });
            navigate(teamLink({
                tournamentId: String(tournamentid),
                teamId: result.toString(),
            }));
        },
    });
    return (_jsxs(ListItem, { disablePadding: true, dense: true, children: [_jsxs(ListItemButton, { dense: true, ...(teamId && tournamentId
                    ? { component: Link, to: teamLink({ tournamentId, teamId }) }
                    : { onClick: () => setOpen(true) }), children: [_jsx(IssuesIcon, { issues: getIssues({
                            clubStatus: club.approvalStatus,
                            contenderIssues: contender?.issues,
                            isClubApproved: club.specialType !== SpecialType.Temp,
                        }), club: club }), _jsxs(ListItemText, { sx: { ml: 1 }, children: [club.name, contender?.team.name && contender.team.name !== club.name ? (_jsxs(Box, { sx: { display: "inline-block", typography: "caption", ml: 0.5 }, children: ["(", contender.team.name, ")"] })) : ("")] }), teamId && (_jsx(Typography, { variant: "body2", children: t(getIssues({
                            clubStatus: club.approvalStatus,
                            contenderIssues: contender?.issues,
                            isClubApproved: club.specialType !== SpecialType.Temp,
                        }).every(({ approved }) => approved)
                            ? "registered"
                            : "registered_init") }))] }), _jsxs(Dialog, { open: isOpen, onClose: () => setOpen(false), children: [_jsx(DialogTitle, { children: t("dialog.title", { name: club.name }) }), isRegable ? (_jsxs("form", { onSubmit: (event) => {
                            event.preventDefault();
                            const data = Object.fromEntries(new FormData(event.currentTarget));
                            const groupId = Number(data.groupId);
                            const classListRequired = groups.find(({ id }) => id === groupId)?.classListRequired;
                            createContender({
                                team: {
                                    name: data.school && data.className
                                        ? `${data.school} ${data.className}`
                                        : club.name,
                                    clubid: club.id,
                                    schoolid: club.schoolid,
                                    classsize: classListRequired ? -1 : 0,
                                },
                                captain: true,
                                groupId,
                                tournamentid: groups.find(({ id }) => id === groupId)
                                    ?.tournamentId,
                                updateClubReason: {
                                    id: club.id,
                                    ...(isClassTournament
                                        ? { schack4anreason: true }
                                        : { yes2chessreason: true }),
                                },
                            });
                        }, children: [_jsx(DialogContent, { children: t("dialog.content", { name: club.name }) }), _jsxs(Box, { sx: { mx: 3 }, children: [_jsx(TextField, { name: "groupId", label: t("dialog.label"), select: true, required: true, fullWidth: true, defaultValue: groups.length === 1 ? groups[0].id : undefined, children: groups.map(({ id, name }) => (_jsx(MenuItem, { value: id, children: name }, id))) }), isClassTournament && (_jsxs(_Fragment, { children: [_jsx(TextField, { name: "school", label: t("dialog.school"), defaultValue: school?.name, required: true, fullWidth: true, sx: { my: 2 }, children: groups.map(({ id, name }) => (_jsx(MenuItem, { value: id, children: name }, id))) }), _jsx(TextField, { name: "className", label: t("dialog.className"), placeholder: t("dialog.className_placeholder"), defaultValue: club.className, required: true, fullWidth: true, children: groups.map(({ id, name }) => (_jsx(MenuItem, { value: id, children: name }, id))) })] }))] }), _jsxs(DialogActions, { children: [_jsx(Button, { color: "inherit", onClick: () => setOpen(false), children: t("dialog.close") }), _jsx(LoadingButton, { type: "submit", loading: isPending, children: t("dialog.submit") })] })] })) : (_jsxs(_Fragment, { children: [_jsx(DialogContent, { children: t("dialog.unableToRegister") }), _jsx(DialogActions, { children: _jsx(Button, { color: "inherit", onClick: () => setOpen(false), children: t("dialog.close") }) })] }))] })] }, club.id));
};
//# sourceMappingURL=RegTeamItem.js.map